import type { MasterDataProductPriceHistory } from "@src/domain/entities/masterData";
import type { MasterDataListFetchResult } from "./useMasterDataListQuery";

export type MasterDataListCacheScope =
  | "purchasers"
  | "purchasers-lookup"
  | "distribution-centers"
  | "purchaser-dc-modal"
  | "products"
  | "product-categories"
  | "product-uoms"
  | "deduction-categories"
  | "deduction-types"
  | "deduction-types-lookup"
  | "promo-types-lookup"
  | "reason-code-mappings"
  | "deduction-subtypes-modal"
  | "product-prices"
  | "product-price-history"
  | "retailers"
  | "retailer-match-rules-modal"
  | "retailer-match-source-columns"
  | "raw-retailer-assoc-modal";

/**
 * Persistent page cache for Master Data lists.
 * Survives section remounts (Purchasers ↔ DC) within the Master Data SPA visit.
 * Cleared when leaving Master Data (layout unmount) or on full browser refresh.
 */
const persistentMasterDataListCache = new Map<
  string,
  MasterDataListFetchResult<unknown>
>();

/** Full price-history payloads. Separate from paginated list entries. */
const persistentProductPriceHistoryCache = new Map<
  string,
  MasterDataProductPriceHistory
>();
const inflightProductPriceHistory = new Map<
  string,
  Promise<MasterDataProductPriceHistory>
>();
let productPriceHistoryEpoch = 0;
let purchasersLookupEpoch = 0;

export function masterDataListCacheKey(
  scope: MasterDataListCacheScope,
  brandKey: string,
  page: number,
  limit: number,
  search: string,
): string {
  return `${scope}|${brandKey}|${page}|${limit}|${search.trim().toLowerCase()}`;
}

/** Cache key for non-paginated Master Data lists (e.g. Purchasers matrix). */
export function masterDataAllListCacheKey(
  scope: MasterDataListCacheScope,
  brandKey: string,
  search: string,
): string {
  return `${scope}|${brandKey}|all|${search.trim().toLowerCase()}`;
}

/** Cache key for purchaser DC modal pages (scoped by purchaserId). */
export function masterDataPurchaserDcCacheKey(
  brandKey: string,
  purchaserId: number,
  page: number,
  limit: number,
): string {
  return `purchaser-dc-modal|${brandKey}|${purchaserId}|${page}|${limit}`;
}

/** Cache key for retailer match-rules modal pages (scoped by retailerId). */
export function masterDataRetailerMatchRulesCacheKey(
  brandKey: string,
  retailerId: number,
  page: number,
  limit: number,
): string {
  return `retailer-match-rules-modal|${brandKey}|${retailerId}|${page}|${limit}`;
}

/** Cache key for retailer match source-column lookup (brand/tenant scoped). */
export function masterDataRetailerMatchSourceColumnsCacheKey(
  brandKey: string,
): string {
  return `retailer-match-source-columns|${brandKey}`;
}

export function getMasterDataRetailerMatchSourceColumnsCache(
  brandKey: string,
): string[] | undefined {
  return getMasterDataListCacheEntry<string>(
    masterDataRetailerMatchSourceColumnsCacheKey(brandKey),
  )?.items;
}

export function setMasterDataRetailerMatchSourceColumnsCache(
  brandKey: string,
  items: string[],
): void {
  setMasterDataListCacheEntry(masterDataRetailerMatchSourceColumnsCacheKey(brandKey), {
    items,
    total: items.length,
    page: 1,
    limit: items.length,
  });
}

/** Cache key for raw retailer association modal pages (scoped by retailerId). */
export function masterDataRawRetailerAssocCacheKey(
  brandKey: string,
  retailerId: number,
  page: number,
  limit: number,
): string {
  return `raw-retailer-assoc-modal|${brandKey}|${retailerId}|${page}|${limit}`;
}

/** Cache key for active purchasers dropdown (brand/tenant scoped). */
export function masterDataPurchasersLookupCacheKey(brandKey: string): string {
  return `purchasers-lookup|v2|${brandKey}`;
}

export function getMasterDataPurchasersLookupCache<TItem>(
  brandKey: string,
): TItem[] | undefined {
  return getMasterDataListCacheEntry<TItem>(
    masterDataPurchasersLookupCacheKey(brandKey),
  )?.items;
}

export function setMasterDataPurchasersLookupCache<TItem>(
  brandKey: string,
  items: TItem[],
): void {
  setMasterDataListCacheEntry(masterDataPurchasersLookupCacheKey(brandKey), {
    items,
    total: items.length,
    page: 1,
    limit: items.length,
  });
}

export function masterDataDeductionTypesLookupCacheKey(brandKey: string): string {
  return `deduction-types-lookup|${brandKey}`;
}

export function getMasterDataDeductionTypesLookupCache<TItem>(
  brandKey: string,
): TItem[] | undefined {
  return getMasterDataListCacheEntry<TItem>(
    masterDataDeductionTypesLookupCacheKey(brandKey),
  )?.items;
}

export function setMasterDataDeductionTypesLookupCache<TItem>(
  brandKey: string,
  items: TItem[],
): void {
  setMasterDataListCacheEntry(masterDataDeductionTypesLookupCacheKey(brandKey), {
    items,
    total: items.length,
    page: 1,
    limit: items.length,
  });
}

export function masterDataPromoTypesLookupCacheKey(brandKey: string): string {
  return `promo-types-lookup|${brandKey}`;
}

export function getMasterDataPromoTypesLookupCache<TItem>(
  brandKey: string,
): TItem[] | undefined {
  return getMasterDataListCacheEntry<TItem>(
    masterDataPromoTypesLookupCacheKey(brandKey),
  )?.items;
}

export function setMasterDataPromoTypesLookupCache<TItem>(
  brandKey: string,
  items: TItem[],
): void {
  setMasterDataListCacheEntry(masterDataPromoTypesLookupCacheKey(brandKey), {
    items,
    total: items.length,
    page: 1,
    limit: items.length,
  });
}

/** Cache key for one product price-history query (brand, product, purchaser, period). */
export function masterDataProductPriceHistoryCacheKey(
  brandKey: string,
  productId: number,
  purchaserScope: string,
  period: string,
): string {
  return `product-price-history|${brandKey}|${productId}|${purchaserScope}|${period}`;
}

export function getMasterDataProductPriceHistoryCache(
  key: string,
): MasterDataProductPriceHistory | undefined {
  return persistentProductPriceHistoryCache.get(key);
}

export function getMasterDataProductPriceHistoryEpoch(): number {
  return productPriceHistoryEpoch;
}

export function getMasterDataPurchasersLookupEpoch(): number {
  return purchasersLookupEpoch;
}

/**
 * Return cached price history, or run `loader` once per key.
 * A write that invalidates `product-price-history` drops in-flight results.
 */
export function loadMasterDataProductPriceHistory(
  key: string,
  loader: () => Promise<MasterDataProductPriceHistory>,
): Promise<MasterDataProductPriceHistory> {
  const cached = persistentProductPriceHistoryCache.get(key);
  if (cached) return Promise.resolve(cached);

  const inflight = inflightProductPriceHistory.get(key);
  if (inflight) return inflight;

  const epoch = productPriceHistoryEpoch;
  const request = loader()
    .then((result) => {
      if (epoch === productPriceHistoryEpoch) {
        persistentProductPriceHistoryCache.set(key, result);
      }
      return result;
    })
    .finally(() => {
      if (inflightProductPriceHistory.get(key) === request) {
        inflightProductPriceHistory.delete(key);
      }
    });

  inflightProductPriceHistory.set(key, request);
  return request;
}

export function masterDataDeductionSubTypesCacheKey(
  brandKey: string,
  parentTypeId: number,
  page: number,
  limit: number,
  search: string,
): string {
  return `deduction-subtypes-modal|${brandKey}|${parentTypeId}|${page}|${limit}|${search.trim().toLowerCase()}`;
}

export function getMasterDataListCacheEntry<TItem>(
  key: string,
): MasterDataListFetchResult<TItem> | undefined {
  return persistentMasterDataListCache.get(key) as
    | MasterDataListFetchResult<TItem>
    | undefined;
}

export function setMasterDataListCacheEntry<TItem>(
  key: string,
  entry: MasterDataListFetchResult<TItem>,
): void {
  persistentMasterDataListCache.set(key, entry as MasterDataListFetchResult<unknown>);
}

function dropProductPriceHistoryCache(): void {
  persistentProductPriceHistoryCache.clear();
  inflightProductPriceHistory.clear();
  productPriceHistoryEpoch += 1;
}

export function clearMasterDataListCache(): void {
  persistentMasterDataListCache.clear();
  dropProductPriceHistoryCache();
  purchasersLookupEpoch += 1;
}

/** Drop cached Master Data list pages for the given scopes (e.g. after a write). */
export function invalidateMasterDataListCacheScopes(
  scopes: MasterDataListCacheScope[],
): void {
  for (const key of persistentMasterDataListCache.keys()) {
    if (scopes.some((scope) => key.startsWith(`${scope}|`))) {
      persistentMasterDataListCache.delete(key);
    }
  }
  if (scopes.includes("product-price-history")) {
    dropProductPriceHistoryCache();
  }
  if (scopes.includes("purchasers-lookup")) {
    purchasersLookupEpoch += 1;
  }
}
